import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, ShieldCheck, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

export const NAV_LINKS = [
    { to: "/", label: "Home", testid: "nav-link-home" },
    { to: "/teams", label: "Teams", testid: "nav-link-teams" },
    { to: "/schedule", label: "Schedule", testid: "nav-link-schedule" },
    { to: "/live", label: "Live", testid: "nav-link-live" },
    { to: "/standings", label: "Standings", testid: "nav-link-standings" },
    { to: "/officials", label: "Officials", testid: "nav-link-officials" },
];

export function Logo() {
    return (
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
            <img
                src="/logo.png"
                alt="YKPBL — Yellowknife Philippine Basketball League"
                className="h-10 w-auto transition-transform duration-300 group-hover:scale-105 drop-shadow-[0_0_18px_rgba(255,85,0,0.35)]"
            />
        </Link>
    );
}

export default function Header() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);

    const linkClass = ({ isActive }) =>
        `font-display font-semibold uppercase tracking-[0.15em] text-sm transition-colors duration-200 ${
            isActive ? "text-[#FF5500]" : "text-slate-300 hover:text-white"
        }`;

    return (
        <header className="sticky top-0 z-50 bg-[#08080C]/85 backdrop-blur-xl border-b border-[#262638]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                <Logo />
                <nav className="hidden md:flex items-center gap-8">
                    {NAV_LINKS.map((l) => (
                        <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={l.testid} className={linkClass}>
                            {l.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="flex items-center gap-3">
                    {user ? (
                        <Link
                            to="/admin"
                            data-testid="nav-admin-dashboard"
                            className="hidden sm:flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] text-white font-display font-bold uppercase tracking-widest text-sm px-4 py-2 rounded-full transition-colors duration-200"
                        >
                            <LayoutDashboard className="w-4 h-4" /> Console
                        </Link>
                    ) : (
                        <Link
                            to="/admin/login"
                            data-testid="nav-admin-login"
                            className="hidden sm:flex items-center gap-2 border border-[#262638] hover:border-[#FF5500] hover:text-[#FF5500] text-slate-300 font-display font-bold uppercase tracking-widest text-sm px-4 py-2 rounded-full transition-colors duration-200"
                        >
                            <ShieldCheck className="w-4 h-4" /> Admin
                        </Link>
                    )}
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button data-testid="nav-mobile-menu-button" className="md:hidden text-slate-200 p-2" aria-label="Open menu">
                                <Menu className="w-6 h-6" />
                            </button>
                        </SheetTrigger>
                        <SheetContent side="right" className="bg-[#12121A] border-[#262638] w-72">
                            <SheetTitle className="font-display font-black text-xl uppercase text-white">YKPBL</SheetTitle>
                            <nav className="flex flex-col gap-5 mt-8">
                                {NAV_LINKS.map((l) => (
                                    <NavLink
                                        key={l.to}
                                        to={l.to}
                                        end={l.to === "/"}
                                        data-testid={`mobile-${l.testid}`}
                                        onClick={() => setOpen(false)}
                                        className={linkClass}
                                    >
                                        {l.label}
                                    </NavLink>
                                ))}
                                <NavLink
                                    to={user ? "/admin" : "/admin/login"}
                                    data-testid="mobile-nav-admin"
                                    onClick={() => setOpen(false)}
                                    className="text-[#FF5500] font-display font-semibold uppercase tracking-[0.15em] text-sm"
                                >
                                    {user ? "Admin Console" : "Admin Login"}
                                </NavLink>
                            </nav>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
